import * as Sentry from "@sentry/react";

let initialized = false;

/** Latest API-bound request id — attached to browser Sentry events for correlation with API logs. */
let lastEdgeRequestId: string | null = null;

/**
 * Record the request id used for an API call and tag the current Sentry scope.
 * Aligns browser errors with API `request_id` tags.
 */
export function setBrowserEdgeRequestIdTag(requestId: string): void {
  lastEdgeRequestId = requestId;
  if (!initialized) return;
  Sentry.getCurrentScope().setTag("request_id", requestId);
}

function readRelease(): string | undefined {
  const fromEnv = import.meta.env.VITE_SENTRY_RELEASE as string | undefined;
  if (fromEnv) return fromEnv;
  try {
    const injected = (window as unknown as { __SENTRY_RELEASE__?: string }).__SENTRY_RELEASE__;
    return injected;
  } catch {
    return undefined;
  }
}

/**
 * Initialize Sentry for the browser. No-op when not in production or when DSN is unset.
 * Call once at app bootstrap (before other error handlers that forward to Sentry).
 */
export function initBrowserSentry(): void {
  if (typeof window === "undefined" || initialized) return;
  if (!import.meta.env.PROD) return;

  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn?.trim()) return;

  const tracesSampleRate = Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE);
  const rate = Number.isFinite(tracesSampleRate) ? Math.min(1, Math.max(0, tracesSampleRate)) : 0;

  try {
    Sentry.init({
      dsn,
      environment:
        (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ?? import.meta.env.MODE,
      release: readRelease(),
      tracesSampleRate: rate,
      sendDefaultPii: false,
      ...(rate > 0 ? { integrations: [Sentry.browserTracingIntegration()] } : {}),
    });
    initialized = true;
  } catch (e) {
    console.error("[Sentry] init failed:", e);
    return;
  }

  // Manual verification in DevTools (production + DSN only): __SENTRY_TEST__()
  (window as unknown as { __SENTRY_TEST__?: () => void }).__SENTRY_TEST__ = () => {
    Sentry.captureException(new Error("Sentry manual test (__SENTRY_TEST__)"));
  };
}

export function isBrowserSentryActive(): boolean {
  return initialized;
}

export function captureBrowserException(error: Error, context?: Record<string, unknown>): void {
  if (!initialized) return;
  Sentry.withScope((scope) => {
    if (lastEdgeRequestId) {
      scope.setTag("request_id", lastEdgeRequestId);
    }
    if (context) {
      for (const [key, value] of Object.entries(context)) {
        scope.setExtra(key, value);
      }
    }
    Sentry.captureException(error);
  });
}

/**
 * Associate the authenticated user with browser Sentry events (user impact counts).
 * No-op when Sentry is not initialized. Email is optional and omitted when unset.
 */
export function identifyBrowserUser(user: { id: string; email?: string | null }): void {
  if (!initialized) return;
  Sentry.setUser({
    id: user.id,
    ...(user.email ? { email: user.email } : {}),
  });
}

/** Clear Sentry user context on logout. */
export function resetBrowserUser(): void {
  if (!initialized) return;
  Sentry.setUser(null);
}
